import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCurrency } from '../../../../utils/formatters';
import ArticuloInfo360DocumentoList from './ArticuloInfo360DocumentoList';
import type { ArticuloInfo360Documento, ArticuloInfo360Indicadores } from '../types';
import { formatCantidad, resolveUnidadMedida } from '../formatCantidad';
import { calcVentasPeriodoAprox } from '../ventasPeriodoAprox';

interface Props {
  data: ArticuloInfo360Indicadores;
  onAbrirDocumento?: (documento: ArticuloInfo360Documento) => void;
}

const PeriodoKpi: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, textAlign: 'center', height: '100%' }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
      {value}
    </Typography>
  </Paper>
);

const ArticuloInfo360TabVentas: React.FC<Props> = ({ data, onAbrirDocumento }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const um = resolveUnidadMedida(data.unidadMedida);
  const hayVentas = data.ventasMensuales.some((item) => item.cantidad !== 0);

  const periodos = useMemo(
    () => calcVentasPeriodoAprox(data.ventasMensuales),
    [data.ventasMensuales],
  );

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          Ventas último período (aprox.)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Períodos aproximados por mes calendario (1 / 2 / 3 meses).
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <PeriodoKpi label="~30 días" value={formatCantidad(periodos.dias30, um)} />
          </Grid>
          <Grid item xs={4}>
            <PeriodoKpi label="~60 días" value={formatCantidad(periodos.dias60, um)} />
          </Grid>
          <Grid item xs={4}>
            <PeriodoKpi label="~90 días" value={formatCantidad(periodos.dias90, um)} />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Evolución de ventas (12 meses){um ? ` · ${um}` : ''}
        </Typography>
        {hayVentas ? (
          <Box sx={{ width: '100%', height: isMobile ? 220 : 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.ventasMensuales} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} width={isMobile ? 48 : 64} />
                <RechartsTooltip
                  formatter={(value: number, name: string) => [
                    name === 'importe'
                      ? formatCurrency(value)
                      : formatCantidad(value, um),
                    name === 'importe' ? 'Importe' : 'Cantidad',
                  ]}
                />
                <Bar dataKey="cantidad" fill="#1976d2" radius={[4, 4, 0, 0]} name="cantidad" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
            No hay ventas registradas en los últimos 12 meses.
          </Typography>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Clientes que más compran
            </Typography>
            <Stack spacing={1.25}>
              {data.clientesTop.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin clientes para mostrar.
                </Typography>
              ) : data.clientesTop.map((c) => (
                <Stack key={`${c.nroCliente}-${c.nombre}`} direction="row" justifyContent="space-between" spacing={1}>
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {c.nombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCantidad(c.cantidad, um)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} whiteSpace="nowrap">
                    {formatCurrency(c.importe)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <ArticuloInfo360DocumentoList
            titulo="Últimas ventas"
            documentos={data.ultimasVentas}
            emptyText="Sin ventas recientes."
            onAbrirDocumento={onAbrirDocumento}
            unidadMedida={um}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ArticuloInfo360TabVentas;
